import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { PatientInitialStateType } from "DataTypes/Redux";
import { EhrDomains } from "Constant/Ehr";

/**
 * Reloads a section's data after a relevant EHR sync finishes.
 *
 * A successful sync announces itself once (see useEhrSync), and every section
 * currently on screen decides for itself whether that announcement applies:
 * it does when the synced domain is this section's own, or when it is "all",
 * which pulls every domain at once. That is what makes one "All EHR Sync"
 * click refresh the whole screen instead of just the card holding the button.
 *
 * Sections behind a closed sidebar modal are unmounted, so they never hear the
 * announcement - and do not need to, since they fetch again on open.
 *
 * @param domain    the domain this section displays, from Constant/Ehr
 * @param onSynced  the section's own fetch function
 */
const useEhrRefresh: (
  domain: string,
  onSynced?: () => void | Promise<void>
) => void = (domain, onSynced) => {
  const { ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );

  // Held in a ref so a section redefining its fetch function on every render
  // does not re-run the effect below.
  const onSyncedRef = useRef(onSynced);
  useEffect(() => {
    onSyncedRef.current = onSynced;
  });

  const count = ehr?.syncSignal?.count || 0;
  const syncedDomain = ehr?.syncSignal?.domain || "";

  // Seeded with whatever count is already there at mount, so a section that
  // has just fetched on open does not immediately fetch a second time.
  const handledRef = useRef(count);

  // Only the count drives this. syncedDomain is read when it fires, and is
  // deliberately not a dependency: it changes alongside count on every sync,
  // so listing it would add nothing but a second reason to re-run.
  useEffect(() => {
    if (!count || count == handledRef.current) return;
    handledRef.current = count;
    if (syncedDomain == domain || syncedDomain == EhrDomains.ALL) {
      onSyncedRef.current?.();
    }
  }, [count]);
};

export default useEhrRefresh;
